import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NavBar, Toast, Skeleton, List, Cell, Empty, Button, Field, Rate, ActionSheet, Divider } from 'react-vant';
import { ArrowLeft, Edit, Star, StarO, ChatO, ClockO } from '@react-vant/icons';
import { getRegistrations, createEvaluation, getStudentEvaluations, getSessionDetail } from '../../../api/club';
import dayjs from 'dayjs';

const ClubEvaluation = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [students, setStudents] = useState([]);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEval, setShowEval] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [evalHistory, setEvalHistory] = useState([]);
  
  // 表单状态
  const [score, setScore] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [sessionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [studentsRes, sessionRes] = await Promise.all([
        getRegistrations({ session_id: sessionId, page_size: 1000 }),
        getSessionDetail(sessionId)
      ]);
      console.log('获取到的学生列表:', studentsRes);
      // 过滤掉已取消报名的学生
      const activeStudents = (studentsRes.items || []).filter(s => s.status !== 'cancelled');
      setStudents(activeStudents);
      setSession(sessionRes);
    } catch (error) {
      console.error('加载评价数据失败:', error);
      Toast.fail('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const openEval = async (student) => {
    setSelectedStudent(student);
    setShowEval(true);
    setScore(5);
    setComment('');
    
    // 获取历史评价
    try {
      const history = await getStudentEvaluations(student.student_id, sessionId);
      setEvalHistory(history || []);
    } catch (error) {
      console.error('获取历史评价失败');
    }
  };

  const handleSubmit = async () => {
    if (!comment.trim()) {
      Toast.info('请输入评价内容');
      return;
    }
    
    try {
      setSubmitting(true);
      await createEvaluation({
        session_id: parseInt(sessionId),
        student_id: selectedStudent.student_id,
        score: score,
        comment: comment.trim(),
        evaluation_date: dayjs().format('YYYY-MM-DD')
      });
      Toast.success('提交评价成功');
      setShowEval(false);
    } catch (error) {
      Toast.fail('提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <NavBar 
        title={<span style={{ fontWeight: 800 }}>学生评价</span>} 
        leftText={<ArrowLeft size={20} />} 
        onClickLeft={() => navigate(-1)}
        fixed
        placeholder
      />

      <div style={{ padding: '16px' }}>
        <div style={{ 
          background: 'linear-gradient(135deg, #7c3aed 0%, #c4b5fd 100%)', 
          borderRadius: '16px', 
          padding: '20px', 
          color: '#fff',
          marginBottom: '20px',
          boxShadow: '0 8px 20px rgba(124, 58, 237, 0.2)'
        }}>
          <div style={{ fontSize: '18px', fontWeight: 800, marginBottom: '4px' }}>
            {session ? session.club_name : '加载中...'}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.9 }}>
            共 {students.length} 名学生，您可以对他们的课堂表现进行综合评价。
          </div>
        </div>

        {loading ? (
          <Skeleton title row={10} />
        ) : students.length === 0 ? (
          <Empty description="暂无确认报名的学生" />
        ) : (
          <div style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden' }}>
            {students.map((item, index) => (
              <Cell
                key={item.id}
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: 700, fontSize: '16px' }}>{item.student_name}</span>
                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>{item.grade}{item.class_name}</span>
                  </div>
                }
                label={item.student_no}
                isLink
                onClick={() => openEval(item)}
                border={index !== students.length - 1}
                icon={<Edit style={{ color: '#7c3aed' }} />}
                center
              />
            ))}
          </div>
        )}
      </div>

      <ActionSheet
        visible={showEval}
        onCancel={() => setShowEval(false)}
        title={selectedStudent ? `评价：${selectedStudent.student_name}` : '学生评价'}
        closeable
        duration={200}
        onClose={() => setShowEval(false)}
      >
        <div style={{ padding: '16px 16px 32px 16px' }}>
          {/* 评价历史 */}
          {evalHistory.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>评价历史</div>
              <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                {evalHistory.map(h => (
                  <div key={h.id} style={{ padding: '10px', background: '#f8fafc', borderRadius: '8px', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <Rate value={h.score} readonly size={12} />
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>{dayjs(h.evaluation_date).format('MM-DD')}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{h.comment}</div>
                  </div>
                ))}
              </div>
              <Divider />
            </div>
          )}

          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>打分 (1-5星)</div>
            <Rate value={score} onChange={setScore} icon={<Star />} voidIcon={<StarO />} color="#7c3aed" />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>评价内容</div>
            <Field
              value={comment}
              onChange={setComment}
              type="textarea"
              rows={3}
              placeholder="请描述学生的表现、优点或建议..."
              border
              style={{ background: '#f8fafc', borderRadius: '8px' }}
            />
          </div>

          <Button 
            type="primary" 
            block 
            loading={submitting} 
            onClick={handleSubmit}
            style={{ borderRadius: '12px', background: '#7c3aed', borderColor: '#7c3aed' }}
          >
            提交评价
          </Button>
        </div>
      </ActionSheet>
    </div>
  );
};

export default ClubEvaluation;
